/**
 * 운영일지 엑셀 — 측정값까지 한 줄씩
 * ---------------------------------------------------------------------------
 * ## 왜 일지 한 건이 한 줄이 아닌가
 * 예전에는 일지 한 건이 한 줄이었다. 그 줄에는 날짜·날씨·작성자처럼 **표지에
 * 적는 값**만 들어가고, 정작 쓸모 있는 **측정값은 빠졌다** — 배출구별
 * 가동시간, 시설별 전력사용량, 수질분석 결과가 그렇다. 폐수 위탁만 예외로
 * 몇 칸이 붙어 있었다.
 *
 * 그런데 측정값은 일지 한 건에 **여러 줄**이다(배출구가 셋이면 세 줄).
 * 한 줄에 우겨넣으려면 「배출구1_가동시간, 배출구2_가동시간…」처럼 칸을
 * 늘려야 하고, 시설이 몇 개인지는 사업장마다 다르니 칸 수가 고정되지 않는다.
 *
 * 그래서 **측정 한 줄이 엑셀 한 행**이 된다. 표지 값은 행마다 되풀이한다.
 * 되풀이가 낭비처럼 보이지만, 이 모양이라야 엑셀에서 거르고 피벗을 돌릴 수
 * 있다 — 「10월 배출구별 평균 가동시간」 같은 것이 한 번에 나온다.
 *
 * ## 구분 칸
 * 한 유형 안에도 성격이 다른 줄이 섞인다(대기: 배출시설 / 방지시설).
 * 맨 앞 「구분」을 보고 거르면 된다. 칸은 그 유형에 나오는 모든 구분의
 * 합집합이라, 자기 구분과 상관없는 칸은 비어 있다 — 그게 정상이다.
 *
 * ## 측정값이 없는 일지
 * 줄이 하나도 없어도 **표지만 담은 한 행**을 낸다. 빼 버리면 "그날 일지를
 * 안 썼다" 와 "썼는데 측정값을 안 적었다" 가 구분되지 않는다.
 */

import type { CsvColumn } from './csv';
import type {
  AnalysisRow,
  DailyLogDetail,
  HourFlags,
  LogType,
} from '@eco/shared';
import { LOG_STATUS_LABELS } from '@eco/shared';

/** 엑셀 한 행. 표지 값 + 그 줄의 측정값. */
export interface ExportRow {
  log: DailyLogDetail;
  /** 「배출시설」·「수질분석」처럼 이 줄이 어디서 왔는지 */
  section: string;
  /** 시설명·측정지점·약품명 등 그 줄을 가리키는 이름 */
  name: string | null;
  /** 구분별 값. 아래 컬럼 정의가 꺼내 쓴다. */
  v: Record<string, string | number | null | undefined>;
}

/** 24칸 가동표를 "1,2,3시" 가 아니라 사람이 읽는 구간으로 줄인다. */
function hoursText(hours: HourFlags | undefined): string {
  if (!hours?.length) return '';
  const on: string[] = [];
  let start = -1;

  for (let h = 0; h <= hours.length; h += 1) {
    const live = h < hours.length && hours[h];
    if (live && start < 0) start = h;
    if (!live && start >= 0) {
      // 배열 0번이 1시다. 사람에게는 「1~5시」로 보여 준다.
      on.push(start + 1 === h ? `${start + 1}시` : `${start + 1}~${h}시`);
      start = -1;
    }
  }
  return on.join(' ');
}

/** 켜진 시간 수. 합계를 내려는 사람이 쓴다. */
function hoursCount(hours: HourFlags | undefined): number | null {
  return hours?.length ? hours.filter(Boolean).length : null;
}

const analysisValues = (a: AnalysisRow) => ({
  pH: a.phValue,
  BOD: a.bodValue,
  TOC: a.tocValue,
  COD: a.codValue,
  SS: a.ssValue,
  nHex: a.nHexValue,
  CN: a.cnValue,
  Cu: a.cuValue,
  사용자1: a.userDef1,
  사용자2: a.userDef2,
  사용자3: a.userDef3,
  사용자4: a.userDef4,
});

/* =========================================================================
 * 일지 → 행들
 * ====================================================================== */

function airRows(log: DailyLogDetail): ExportRow[] {
  const rows: ExportRow[] = [];

  for (const o of log.airOutlets ?? []) {
    rows.push({
      log, section: '배출시설', name: o.facilityName,
      v: {
        배출구: o.outletNo,
        가동시작: o.uptimeStart, 가동종료: o.uptimeEnd, 가동시간: o.operatingHours,
        원료명: o.materialName, 원료사용량: o.materialUsage, 원료단위: o.materialUnit,
        비고: o.note,
      },
    });
  }

  for (const p of log.airPreventions ?? []) {
    rows.push({
      log, section: '방지시설', name: p.facilityName,
      v: {
        배출구: p.outletNo,
        설치위치: p.installLocation,
        전력사용량: p.powerConsumption, 정격전력: p.powerCapacity,
        처리용량: p.treatmentCapacity, 오염물질: p.pollutant,
        처리농도: p.concentration, 처리효율: (p as { efficiency?: number | null }).efficiency,
        비고: (p as { note?: string | null }).note,
      },
    });
  }

  return rows;
}

function consignRows(log: DailyLogDetail): ExportRow[] {
  const rows: ExportRow[] = [];
  const c = log.waterConsign;

  if (c) {
    rows.push({
      log, section: '위탁운영', name: c.facilityName,
      v: {
        가동시작: c.uptimeStart, 가동종료: c.uptimeEnd, 가동시간: c.operatingHours,
        용수사용량: c.waterVolume, 폐수발생량: c.wastewaterGeneration,
        위탁량: c.consignmentAmount, 보관량: c.storageWastewater,
        인계서번호: c.confirmationSerialNo,
        처리업체: c.processingCompany, 처리업체등록번호: c.processingCompanyRegNo,
        비고: c.note,
      },
    });
  }

  for (const m of log.consignMaterials ?? []) {
    rows.push({
      log, section: '원료사용', name: m.materialName,
      v: { 수량: m.quantity, 단위: m.unit, 비고: m.note },
    });
  }

  return rows;
}

function selfRows(log: DailyLogDetail): ExportRow[] {
  const rows: ExportRow[] = [];
  const s = log.waterSelf;

  if (s) {
    rows.push({
      log, section: '운영개요', name: null,
      v: {
        전력가동시간: s.powerOperatingTime, 전력사용량: s.powerUsageAmount,
        '폐수량당전력': s.powerPerWater, 전력누계: s.powerTotalGauge,
        '폭기조pH': s.aerationPh, 폭기조수온: s.aerationWaterTemp,
        'DO': s.aerationDo, 'SV30': s.aerationSv30, 'MLSS': s.aerationMlss,
        근무자: s.shiftWorkers, 비고: s.powerRemarks,
      },
    });
  }

  for (const f of log.facilityHours ?? []) {
    rows.push({
      log, section: '배출시설 가동', name: f.facilityName,
      v: { 가동시간대: hoursText(f.hours), 가동시간: hoursCount(f.hours), 비고: f.note },
    });
  }

  for (const p of log.preventionHours ?? []) {
    rows.push({
      log, section: '방지시설 가동', name: p.facilityName,
      v: {
        가동시간대: hoursText(p.hours), 가동시간: hoursCount(p.hours),
        운전자: p.workerName, 비고: p.note,
      },
    });
  }

  for (const w of log.waterUsages ?? []) {
    rows.push({
      log, section: '용수사용', name: w.category,
      v: {
        전월지침: w.prevGauge, 당월지침: w.currGauge, 사용량: w.usageAmount,
        검침시각: w.checkTime, 비고: w.note,
      },
    });
  }

  for (const g of log.sludges ?? []) {
    rows.push({
      log, section: '슬러지', name: g.storageLocation,
      v: {
        발생량: g.generationAmount, 처리량: g.treatmentAmount, 보관량: g.storageAmount,
        함수율: g.waterContent, 자가처리장소: g.selfTreatmentLocation,
        위탁업체: g.outsourcedCompany, 비고: g.note,
      },
    });
  }

  for (const m of log.selfMaterials ?? []) {
    rows.push({
      log, section: '원료사용', name: m.materialName,
      v: { 수량: m.quantity, 단위: m.unit, 비고: m.note },
    });
  }

  for (const c of log.chemicals ?? []) {
    rows.push({
      log, section: '약품사용', name: c.chemicalName,
      v: {
        구입량: c.purchaseAmount, 사용량: c.usageAmount, 재고량: c.remainingAmount,
        단위: c.unit, 비고: c.remarks,
      },
    });
  }

  for (const a of log.analyses ?? []) {
    rows.push({
      log, section: '수질분석', name: a.category,
      v: { ...analysisValues(a), 분석시각: a.analyzedAt, 비고: a.valueNote },
    });
  }

  return rows;
}

/** 측정 줄이 하나도 없는 일지도 표지만으로 한 행을 남긴다. */
function headerOnly(log: DailyLogDetail): ExportRow {
  return { log, section: '(측정값 없음)', name: null, v: {} };
}

export function toExportRows(logs: DailyLogDetail[], logType: LogType): ExportRow[] {
  const build =
    logType === 'T01_AIR' ? airRows : logType === 'T02_WATER_CONSIGN' ? consignRows : selfRows;

  return logs.flatMap((log) => {
    const rows = build(log);
    return rows.length > 0 ? rows : [headerOnly(log)];
  });
}

/* =========================================================================
 * 행들 → 엑셀 칸
 * ====================================================================== */

/** 모든 행에 되풀이되는 표지 칸. 거를 때 기준이 된다. */
const HEADER_COLUMNS: Array<CsvColumn<ExportRow>> = [
  { header: '작성일자', value: (r) => r.log.logDate },
  { header: '사업장', value: (r) => r.log.siteName },
  { header: '구분', value: (r) => r.section },
  { header: '시설·항목', value: (r) => r.name },
];

/** 맨 뒤에 붙는 표지 칸. 측정값을 먼저 보게 뒤로 뺀다. */
const TRAILER_COLUMNS: Array<CsvColumn<ExportRow>> = [
  { header: '날씨', value: (r) => r.log.weather },
  { header: '기온(℃)', value: (r) => r.log.temperature },
  { header: '상태', value: (r) => LOG_STATUS_LABELS[r.log.status] },
  { header: '작성자', value: (r) => r.log.writerName },
  { header: '확인자', value: (r) => r.log.confirmerName },
  { header: '확인일시', value: (r) => r.log.confirmedAt },
  { header: '일지 비고', value: (r) => r.log.note },
];

/**
 * 유형별 측정값 칸.
 *
 * 그 유형에 나올 수 있는 **모든 구분의 합집합**이다. 자기 구분과 상관없는
 * 칸은 비어 있다. 칸을 구분마다 따로 만들면 시트가 여러 장이 되는데,
 * CSV 는 한 장뿐이라 한 표에 담는다.
 */
const VALUE_KEYS: Record<LogType, string[]> = {
  T01_AIR: [
    '배출구', '가동시작', '가동종료', '가동시간',
    '원료명', '원료사용량', '원료단위',
    '설치위치', '전력사용량', '정격전력', '처리용량', '오염물질', '처리농도', '처리효율',
    '비고',
  ],
  T02_WATER_CONSIGN: [
    '가동시작', '가동종료', '가동시간',
    '용수사용량', '폐수발생량', '위탁량', '보관량',
    '인계서번호', '처리업체', '처리업체등록번호',
    '수량', '단위', '비고',
  ],
  T03_WATER_SELF: [
    '가동시간대', '가동시간', '운전자',
    '전력가동시간', '전력사용량', '폐수량당전력', '전력누계',
    '폭기조pH', '폭기조수온', 'DO', 'SV30', 'MLSS', '근무자',
    '전월지침', '당월지침', '사용량', '검침시각',
    '발생량', '처리량', '보관량', '함수율', '자가처리장소', '위탁업체',
    '구입량', '재고량', '수량', '단위',
    'pH', 'BOD', 'TOC', 'COD', 'SS', 'nHex', 'CN', 'Cu',
    '사용자1', '사용자2', '사용자3', '사용자4', '분석시각',
    '비고',
  ],
};

export function exportColumns(logType: LogType): Array<CsvColumn<ExportRow>> {
  const values: Array<CsvColumn<ExportRow>> = VALUE_KEYS[logType].map((key) => ({
    header: key,
    value: (r) => r.v[key] ?? null,
  }));

  return [...HEADER_COLUMNS, ...values, ...TRAILER_COLUMNS];
}
